// @LTG-ID: data/journals/selling-an-auto-repair-shop
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 37 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-an-auto-repair-shop",
    FRONTMATTER: {
        number: "74",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling an Auto Repair Shop: From Owner-Operator Garage to Sellable Business",
        deck: "Auto repair shops sell best when the owner is out of the bays, technicians and a service advisor run daily operations, and a shop management system holds the customer history.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-30",
        publishedHuman: "June 30, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 476,
        timeRequired: "PT3M",
        keywords: "selling an auto repair shop, auto repair business valuation, selling a mechanic shop, auto shop for sale preparation",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Auto repair shops sell best when the owner is out of the bays, technicians and a service advisor run daily operations, and a shop management system holds the customer history. Buyers examine technician retention, car count trends, and the property situation, including environmental due diligence common for automotive sites. The auto repair industry contains two kinds of businesses that look identical from the street: shops that are companies, and garages that are one skilled person's job with a ",
        complianceNote: "This article is educational and does not constitute legal, environmental, or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "owner-dependence-business-value",
        "business-systems-processes-value",
        "selling-hvac-plumbing-electrical-business"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling an Auto Repair Shop: From Garage to Sellable · LTG",
        canonical: "https://letstalkgrowth.com/journals/selling-an-auto-repair-shop",
        ogImage: "/journals/og/selling-an-auto-repair-shop.jpg",
    },
    BODY_MARKDOWN: `Auto repair shops sell best when the owner is out of the bays, technicians and a service advisor run daily operations, and a shop management system holds the customer history. Buyers examine technician retention, car count trends, and the property situation, including environmental due diligence common for automotive sites.

The auto repair industry contains two kinds of businesses that look identical from the street: shops that are companies, and garages that are one skilled person's job with a building around it. Buyers pay real money for the first and very little for the second, and the difference is built, not found.

## The owner-in-the-bay problem

No industry illustrates this series' owner-dependence theme more literally. When the owner is the master technician, the diagnostician customers ask for, and the service writer besides, the buyer's question answers itself: what exactly transfers at closing? The multi-year preparation for an auto shop sale is therefore mostly a staffing and systems project: technicians who can produce without the owner turning wrenches, a service advisor who owns the customer conversation, and the owner migrated to genuine management, or better, toward the door. The vacation test from our owner-dependence article was practically invented for this industry.

## The assets buyers actually examine

- **The customer file.** A shop management system holding vehicle histories, service reminders, and contact data is the business's memory and its closest thing to recurring revenue; declining-to-steady car count trends are read straight from it. Customer records living in paper tickets or the owner's head are worth what they cost to reconstruct: little.
- **The technician roster.** Scarcity has made retained, certified technicians a headline asset, exactly as in the trades. Tenure, certifications, and pay competitiveness all get evaluated, because the buyer is largely buying the team's continued presence.
- **The property and its history.** Whether owned or leased, automotive sites carry environmental diligence as standard practice: lifts, tanks, fluids, and disposal history attract Phase I environmental site assessments in many deals. Sellers who gather their compliance records, disposal manifests, tank documentation, permits, ahead of time turn a scary-sounding step into a routine one; known issues are for environmental professionals and counsel, engaged early.
- **The mix and the equipment.** General repair, specialty, fleet accounts, and tire or quick-service components each carry different revenue quality, with fleet relationships prized for their contract-like recurrence. Equipment gets the same deferred-maintenance read as in manufacturing: the bays are inspected, and their condition is priced.

## The realistic path

An owner two to three years out builds the team, installs and actually uses a shop management system, cultivates fleet accounts, assembles the environmental paperwork, and gets the books to the provable standard this series describes everywhere. That owner sells a business. The owner who skips it sells equipment and a lease, and the market prices the difference without sentiment.`,
    FAQ: [
        { question: "Why do auto repair shops sell for such different prices?", answer: "Because some are transferable businesses, with technician teams, service advisors, and customer data systems, and some are the owner's personal production with a building attached. Buyers pay for what continues without the seller." },
        { question: "What is environmental due diligence on an auto shop?", answer: "Buyers and lenders commonly commission environmental site assessments on automotive properties, reviewing tanks, fluids handling, and disposal history. Sellers who organize compliance records in advance, and address known issues with professionals early, keep this step routine." },
        { question: "Do fleet accounts increase a shop's value?", answer: "Yes. Fleet maintenance relationships behave like contracts, recurring, volume-stable, and transferable, which is exactly the revenue quality buyers pay a premium for across every industry." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
            meta: "3 min · Brief 45",
            to: "/journals/owner-dependence-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Systems and Processes: The Unglamorous Work That Makes a Business Sellable",
            meta: "3 min · Brief 53",
            to: "/journals/business-systems-processes-value",
        },
        {
            tag: "Industry-Specific",
            title: "Selling an HVAC, Plumbing, or Electrical Business: What Trades Owners Should Know",
            meta: "3 min · Brief 68",
            to: "/journals/selling-hvac-plumbing-electrical-business",
        },
    ],
};

export default BRIEF;
